import {
  Box,
  Button,
  Grid2,
  TextField,
  Typography
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { Formik } from "formik";
import moment from "moment";
import { useState } from "react";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import { updateProcedure } from "../../api/procedure";
import { useTranslationHook } from "../../hooks";
import { useFormStyles } from "../../hooks/useFormStyles";
import { setLoading } from "../../store/UI";
import {
  Procedure,
  UpdateProcedureRequestSchema,
} from "../../types/model/procedure";

interface ProcedureDetailFormProps {
  accountId: number;
  projectId: number;
  activityId: number;
  procedure: Procedure | undefined;
  onUpdate?: (procedure: Procedure) => void;
  readOnly: boolean;
}

const ProcedureDetailForm = ({
  accountId,
  projectId,
  activityId,
  procedure,
  onUpdate,
  readOnly
}: ProcedureDetailFormProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslationHook();
  const [editing, setEditing] = useState(false);
  const formStyles = useFormStyles();

  const getFieldVisibility = (field: string): "hidden" | "optional" | "required" => {
    const config = procedure?.fieldConfig?.[field];
    if (config === 0) return "hidden";
    if (config === 2) return "required";
    return "optional";
  };

  const startDateVisibility = getFieldVisibility("startDate") as string;
  const endDateVisibility = getFieldVisibility("endDate") as string;

  const handleUpdateProcedure = async (values: any) => {
    try {
      dispatch(setLoading(true));
      const request = UpdateProcedureRequestSchema.parse({
        name: values.name,
        description: values.description,
        startDate: values.startDate,
        endDate: values.endDate,
        accountId,
        ownerId: procedure?.ownerId,
      });
      const response = await updateProcedure(projectId, activityId, procedure?.id!, request);
      onUpdate?.(response);
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(setLoading(false));
      setEditing(false);
    }
  };

  return (
    <Box>
      <Formik
        initialValues={{
          name: procedure?.name ?? "",
          description: procedure?.description ?? "",
          startDate: procedure?.startDate ?? null,
          endDate: procedure?.endDate ?? null,
        }}
        onSubmit={(values) => {
          dispatch(setLoading(true));
          handleUpdateProcedure(values);
        }}
        validationSchema={Yup.object().shape({
          name: Yup.string().required(t("procedures.name-required")),
          description: Yup.string().required(t("procedures.description-required")),
          startDate: Yup.date()
            .nullable()
            .typeError(t("admin.projects.startDate-valid")),
          endDate: Yup.date()
            .nullable()
            .typeError(t("admin.projects.endDate-valid"))
            .when("startDate", (startDate, schema) =>
              startDate?.[0]
                ? schema.min(startDate[0], t("admin.projects.endDate-after-startDate"))
                : schema
            ),
        })}
      >
        {({ values, touched, errors, handleChange, handleBlur, handleSubmit, setFieldValue }) => (
          <Box component="form" onSubmit={handleSubmit} sx={{ p: 1 }}>
            <Grid2 container spacing={3}>
              <Grid2 size={12}>
                <TextField
                  fullWidth
                  required
                  variant={editing ? "outlined" : "standard"}
                  disabled={!editing}
                  id="name"
                  name="name"
                  label={t("procedures.name")}
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!(errors.name && touched.name)}
                  helperText={errors.name || ""}
                  sx={formStyles}
                />
              </Grid2>

              {startDateVisibility !== "hidden" && endDateVisibility !== "hidden" && (
                <Grid2 size={12}>
                  <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 700, mb: 2 }}>
                    {t("projects.dates")}
                  </Typography>

                  <Grid2 container spacing={2}>
                    {startDateVisibility !== "hidden" && (
                      <Grid2 size={6}>
                        <DatePicker
                          label={t("procedures.start-date")}
                          disabled={!editing}
                          timezone="UTC"
                          value={values.startDate ? moment(values.startDate) : null}
                          onChange={(value) =>
                            setFieldValue("startDate", value ? value.toDate() : null)
                          }
                          slotProps={{
                            field: { clearable: true },
                            textField: {
                              fullWidth: true,
                              variant: editing ? "outlined" : "standard",
                              required: startDateVisibility === "required",
                              error: !!(errors.startDate && touched.startDate),
                              helperText: touched.startDate && errors.startDate ? errors.startDate : "",
                              sx: formStyles,
                            },
                          }}
                          format="DD/MM/YYYY"
                          views={["day", "month", "year"]}
                        />
                      </Grid2>
                    )}

                    {endDateVisibility !== "hidden" && (
                      <Grid2 size={6}>
                        <DatePicker
                          label={t("procedures.end-date")}
                          disabled={!editing}
                          timezone="UTC"
                          value={values.endDate ? moment(values.endDate) : null}
                          onChange={(value) =>
                            setFieldValue("endDate", value ? value.toDate() : null)
                          }
                          slotProps={{
                            field: { clearable: true },
                            textField: {
                              fullWidth: true,
                              variant: editing ? "outlined" : "standard",
                              required: endDateVisibility === "required",
                              error: !!(errors.endDate && touched.endDate),
                              helperText: touched.endDate && errors.endDate ? errors.endDate : "",
                              sx: formStyles,
                            },
                          }}
                          format="DD/MM/YYYY"
                          views={["day", "month", "year"]}
                        />
                      </Grid2>
                    )}
                  </Grid2>
                </Grid2>
              )}

              <Grid2 size={12}>
                <TextField
                  fullWidth
                  required
                  multiline
                  rows={4}
                  variant={editing ? "outlined" : "standard"}
                  disabled={!editing}
                  id="description"
                  name="description"
                  label={t("procedures.description")}
                  value={values.description}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!(errors.description && touched.description)}
                  helperText={touched.description && errors.description}
                  sx={formStyles}
                />
              </Grid2>

              {!readOnly && (
                <Grid2
                  size={12}
                  sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}
                >
                  {editing ? (
                    <>
                      <Button
                        variant="outlined"
                        color="secondary"
                        onClick={() => setEditing(false)}
                      >
                        {t("generic.cancel")}
                      </Button>
                      <Button
                        type="submit"
                        variant="contained"
                      >
                        {t("generic.save")}
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="contained"
                      disabled={procedure?.canceled || procedure?.closed}
                      onClick={() => setEditing(true)}
                    >
                      {t("generic.edit")}
                    </Button>
                  )}
                </Grid2>
              )}
            </Grid2>
          </Box>
        )}
      </Formik>
    </Box>
  );
};

export default ProcedureDetailForm;